/**
 * Trang tổng quan admin — hiển thị thống kê BĐS, liên hệ và hành động ưu tiên.
 *
 * Luồng dữ liệu:
 * - useAdminDashboard lấy dashboardStats từ API.
 * - Skeleton khi đang tải hoặc chưa có dữ liệu.
 * - statCards: mảng cấu hình thẻ thống kê (icon, theme, mô tả).
 * - DashboardQuickAction: liên kết nhanh tới liên hệ mới / BĐS chờ duyệt.
 */
import { DashboardQuickAction } from '../../components/admin/DashboardQuickAction';
import { DashboardSkeleton } from '../../components/admin/DashboardSkeleton';
import { DashboardStatCard } from '../../components/admin/DashboardStatCard';
import { useAdminDashboard } from '../../features/admin/dashboard/hooks/useAdminDashboard';
import { PUBLICATION_STATUSES } from '../../shared/constants/property';
import { APP_ROUTES } from '../../shared/constants/routes';
import { formatText, TEXT } from '../../shared/languages';

/** Bộ icon SVG dùng chung cho thẻ thống kê và quick action */
const StatIcons = {
  total: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
      <path
        d="M4 7.5A2.5 2.5 0 016.5 5H17.5A2.5 2.5 0 0120 7.5v9A2.5 2.5 0 0117.5 19H6.5A2.5 2.5 0 014 16.5v-9z"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <path d="M8 9h8M8 13h5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  ),
  active: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
      <path
        d="M12 3l2.2 4.5 5 .7-3.6 3.5.9 5-4.5-2.4-4.5 2.4.9-5L4.8 8.2l5-.7L12 3z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  ),
  contacts: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
      <path
        d="M4 6.5A2.5 2.5 0 016.5 4h11A2.5 2.5 0 0120 6.5v11A2.5 2.5 0 0117.5 20h-11A2.5 2.5 0 014 17.5v-11z"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <path d="M7 9h10M7 13h6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  ),
  newContacts: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
      <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  ),
  drafts: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
      <path
        d="M8 4h8l2 3v13a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path d="M9 12h6M9 16h4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  ),
  pending: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
      <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.75" />
      <path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  ),
  contactsAction: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
      <path d="M4 7h16v10H4V7z" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
      <path d="M4 9l8 5 8-5" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
    </svg>
  ),
  pendingAction: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
      <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.75" />
      <path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  ),
};

/** Trang dashboard admin với thống kê tổng quan và quick action */
export const AdminDashboardPage = () => {
  const { dashboardStats, isLoadingDashboard } = useAdminDashboard();
  const admin = TEXT.admin;
  const dashboard = admin.dashboard;
  const stats = admin.stats;

  if (isLoadingDashboard || !dashboardStats) {
    return <DashboardSkeleton />;
  }

  const pendingCount = dashboardStats.pending_properties || 0;
  const newContactsCount = dashboardStats.new_contacts || 0;

  /** Cấu hình các thẻ thống kê — theme 'urgent' khi có việc cần xử lý */
  const statCards = [
    {
      key: 'total',
      label: stats.total,
      value: dashboardStats.total_properties,
      description: dashboard.statTotalDesc,
      icon: StatIcons.total,
      theme: 'default',
    },
    {
      key: 'active',
      label: stats.active,
      value: dashboardStats.active_properties,
      description: dashboard.statActiveDesc,
      icon: StatIcons.active,
      theme: 'success',
    },
    {
      key: 'contacts',
      label: stats.contacts,
      value: dashboardStats.total_contacts,
      description: dashboard.statContactsDesc,
      icon: StatIcons.contacts,
      theme: 'info',
    },
    {
      key: 'newContacts',
      label: stats.newContacts,
      value: newContactsCount,
      description: dashboard.statNewContactsDesc,
      icon: StatIcons.newContacts,
      theme: newContactsCount > 0 ? 'urgent' : 'warning',
    },
    {
      key: 'drafts',
      label: stats.drafts,
      value: dashboardStats.draft_properties || 0,
      description: dashboard.statDraftsDesc,
      icon: StatIcons.drafts,
      theme: 'muted',
    },
    {
      key: 'pending',
      label: stats.pending,
      value: pendingCount,
      description: dashboard.statPendingDesc,
      icon: StatIcons.pending,
      theme: pendingCount > 0 ? 'urgent' : 'warning',
    },
  ];

  /** Mô tả động cho quick action — thay đổi khi có liên hệ/BĐS chờ xử lý */
  const contactsActionDescription =
    newContactsCount > 0
      ? formatText(dashboard.alertContacts, { count: newContactsCount })
      : dashboard.actionContactsDesc;

  const pendingActionDescription =
    pendingCount > 0
      ? formatText(dashboard.alertPending, { count: pendingCount })
      : dashboard.actionPendingDesc;

  return (
    <div className="admin-dashboard-page admin-dashboard-page--minimal">
      <div className="admin-heading admin-dashboard-heading">
        <div>
          <span className="eyebrow">{admin.dashboardEyebrow}</span>
          <h1>{admin.dashboardTitle}</h1>
          <p className="admin-dashboard-heading-subtitle">{dashboard.subtitle}</p>
        </div>
      </div>

      <section className="admin-dashboard-overview">
        <div className="admin-dashboard-section-head">
          <h2>{dashboard.overviewTitle}</h2>
        </div>
        <div className="admin-dashboard-stats admin-dashboard-stats--wide">
          {statCards.map((statCard) => (
            <DashboardStatCard key={statCard.key} {...statCard} />
          ))}
        </div>
      </section>

      <section className="admin-dashboard-priority">
        <div className="admin-dashboard-actions-row">
          <DashboardQuickAction
            to={`${APP_ROUTES.adminContacts}?status=new`}
            title={dashboard.alertContactsAction}
            description={contactsActionDescription}
            icon={StatIcons.contactsAction}
            accent="#0f766e"
          />
          <DashboardQuickAction
            to={`${APP_ROUTES.adminProperties}?publication_status=${PUBLICATION_STATUSES.pending}`}
            title={dashboard.alertPendingAction}
            description={pendingActionDescription}
            icon={StatIcons.pendingAction}
            accent="#d97706"
          />
        </div>
      </section>
    </div>
  );
};
